import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { me, getMyFeatures, getMyEntitlements, setAuthTokens, signout as doSignout, clearAuthTokens, getAccessToken, entitlementBool, entitlementInt } from '../api/client'
import type { AuthUser, FeatureRecord, ResolvedEntitlements } from '../api/client'
import SessionTimeoutModal from '../components/SessionTimeoutModal'

const AUTO_SIGNOUT_MINUTES = Number(import.meta.env.VITE_AUTO_SIGNOUT_MINUTES || 0)
const AUTO_SIGNOUT_MS = AUTO_SIGNOUT_MINUTES > 0 ? AUTO_SIGNOUT_MINUTES * 60 * 1000 : 0
const IDLE_WARNING_SECONDS = Number(import.meta.env.VITE_IDLE_WARNING_SECONDS || 60)

interface AuthCtx {
  user: AuthUser | null
  features: FeatureRecord[]
  entitlements: ResolvedEntitlements | null
  loading: boolean
  refreshMe: () => Promise<void>
  logout: () => Promise<void>
  hasFeature: (code: string) => boolean
  hasEntitlement: (key: string, fallback?: boolean) => boolean
  entitlementLimit: (key: string, fallback?: number) => number
}

const AuthContext = createContext<AuthCtx>({
  user: null, features: [], entitlements: null, loading: true,
  refreshMe: async () => {}, logout: async () => {},
  hasFeature: () => false,
  hasEntitlement: () => false,
  entitlementLimit: () => 0,
})

export function useAuth() { return useContext(AuthContext) }

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [features, setFeatures] = useState<FeatureRecord[]>([])
  const [entitlements, setEntitlements] = useState<ResolvedEntitlements | null>(null)
  const [loading, setLoading] = useState(true)

  // Idle timeout state
  const [idleWarning, setIdleWarning] = useState(false)
  const [idleCountdown, setIdleCountdown] = useState(IDLE_WARNING_SECONDS)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warnIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const idleWarningRef = useRef(false)
  useEffect(() => { idleWarningRef.current = idleWarning }, [idleWarning])

  const loadMe = useCallback(async () => {
    setLoading(true)
    try {
      const u = await me()
      // Treat a response missing the identity fields the UI relies on as
      // unauthenticated, even if the backend returned 200. Defends the navbar
      // and protected routes from a degenerate session showing a "logged-in"
      // shell with a "?" avatar and no real user behind it.
      const valid = u && u.id && (u.username || u.email)
      if (valid) {
        setUser(u)
        const [feats, ents] = await Promise.all([
          getMyFeatures().catch(() => [] as FeatureRecord[]),
          getMyEntitlements().catch(() => null as ResolvedEntitlements | null),
        ])
        setFeatures(feats)
        setEntitlements(ents)
      } else {
        setUser(null)
        setFeatures([])
        setEntitlements(null)
        if (u) { try { clearAuthTokens() } catch (_) {} }
      }
    } catch (_) {
      setUser(null)
      setFeatures([])
      setEntitlements(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Pick up tokens from URL (OAuth success handler redirects with ?token=&refreshToken=)
    try {
      const p = new URLSearchParams(window.location.search)
      const token = p.get('token'), refresh = p.get('refreshToken')
      const url = new URL(window.location.href)
      if (token || refresh) {
        setAuthTokens({ accessToken: token ?? undefined, refreshToken: refresh ?? undefined })
        url.searchParams.delete('token'); url.searchParams.delete('refreshToken')
        window.history.replaceState({}, '', url.toString())
      }
    } catch (_) {}
    loadMe()
  }, [loadMe])

  // Revalidate session when the tab becomes visible again.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) return
      const hasToken = Boolean(getAccessToken())
      if (user && !hasToken) {
        clearAuthTokens()
        setUser(null)
        setFeatures([])
        setEntitlements(null)
      } else if (user && hasToken) {
        loadMe()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [user, loadMe])

  const doSignoutAndRedirect = useCallback(() => {
    // Fire-and-forget: the API call finishes in the background while the UI
    // redirects immediately. An earlier version awaited doSignout() before
    // clearing tokens + navigating, which could hang the whole session-timeout
    // modal / logout UI if the backend was slow.
    setIdleWarning(false)
    if (warnIntervalRef.current) clearInterval(warnIntervalRef.current)
    if (forceSignoutRef.current) clearTimeout(forceSignoutRef.current)
    doSignout().catch(() => undefined)
    try { clearAuthTokens() } catch (_) {}
    setUser(null)
    setFeatures([])
    setEntitlements(null)
    window.location.replace('/signin')
  }, [])

  const logout = useCallback(async () => {
    await doSignoutAndRedirect()
  }, [doSignoutAndRedirect])

  const hasFeature = useCallback((code: string) => {
    return features.some((f) => f.code === code)
  }, [features])

  const hasEntitlement = useCallback((key: string, fallback = false) => {
    return entitlementBool(entitlements, key, fallback)
  }, [entitlements])

  const entitlementLimit = useCallback((key: string, fallback = 0) => {
    return entitlementInt(entitlements, key, fallback)
  }, [entitlements])

  const deadlineRef = useRef<number>(0)
  const forceSignoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Idle timeout effect
  useEffect(() => {
    if (!AUTO_SIGNOUT_MS || !user) return

    const startWarnCountdown = () => {
      const deadline = Date.now() + IDLE_WARNING_SECONDS * 1000
      deadlineRef.current = deadline
      setIdleCountdown(IDLE_WARNING_SECONDS)
      setIdleWarning(true)

      if (forceSignoutRef.current) clearTimeout(forceSignoutRef.current)
      forceSignoutRef.current = setTimeout(() => {
        doSignoutAndRedirect()
      }, IDLE_WARNING_SECONDS * 1000)

      if (warnIntervalRef.current) clearInterval(warnIntervalRef.current)
      warnIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
        setIdleCountdown(remaining)
        if (remaining <= 0) clearInterval(warnIntervalRef.current!)
      }, 500)
    }

    const resetTimer = () => {
      if (idleWarningRef.current) return
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(startWarnCountdown, AUTO_SIGNOUT_MS)
    }

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
    events.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }))
    resetTimer()

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (warnIntervalRef.current) clearInterval(warnIntervalRef.current)
      if (forceSignoutRef.current) clearTimeout(forceSignoutRef.current)
      events.forEach((evt) => window.removeEventListener(evt, resetTimer))
    }
  }, [user, doSignoutAndRedirect])

  const extendSession = useCallback(() => {
    setIdleWarning(false)
    setIdleCountdown(IDLE_WARNING_SECONDS)
    if (warnIntervalRef.current) clearInterval(warnIntervalRef.current)
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (forceSignoutRef.current) clearTimeout(forceSignoutRef.current)
    if (AUTO_SIGNOUT_MS) {
      idleTimerRef.current = setTimeout(() => {
        setIdleCountdown(IDLE_WARNING_SECONDS)
        setIdleWarning(true)
      }, AUTO_SIGNOUT_MS)
    }
  }, [])

  const confirmSignout = useCallback(async () => {
    if (warnIntervalRef.current) clearInterval(warnIntervalRef.current)
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    await doSignoutAndRedirect()
  }, [doSignoutAndRedirect])

  return (
    <AuthContext.Provider value={{ user, features, entitlements, loading, refreshMe: loadMe, logout, hasFeature, hasEntitlement, entitlementLimit }}>
      {children}
      <SessionTimeoutModal
        visible={idleWarning}
        countdown={idleCountdown}
        onExtend={extendSession}
        onSignout={confirmSignout}
      />
    </AuthContext.Provider>
  )
}
